import clsx from 'clsx';
import {
  ElementType,
  forwardRef,
  MouseEventHandler,
  type ReactElement,
  ReactEventHandler,
  type ReactNode,
} from 'react';

import { Button } from '@dynatrace/strato-components/buttons';
import {
  type PolymorphicComponentProps,
  type DataTestId,
  type StylingProps,
} from '@dynatrace/strato-components/core';

import * as styles from './ActionButton.sty.js';
import { useActionGroupContext } from './hooks/useActionGroupContext.js';
import { useFocusRing } from '../../../core/styles/useFocusRing.js';
import { Menu } from '../../../navigation/menu/Menu.js';
import { textStyleCSS } from '../../../styles/textStyle.sty.js';

// #region Typings
/**
 * @public
 */
export interface ActionButtonOwnProps extends StylingProps, DataTestId {
  /**
   * Whether the action button is disabled.
   */
  disabled?: boolean;
  /**
   * Whether or not the ActionButton label is shown in the expanded header.
   * If set to false, the label is hidden and only shows in the collapsed menu.
   * If set to false and the prefixIcon is undefined, the label will be shown regardless.
   * @defaultValue true
   */
  showLabel?: boolean;
  /** If set to true, the item will not be moved into the menu in case of insufficient space. */
  pin?: boolean;
  /** Icon shown before the text of the ActionButton. */
  prefixIcon?: ReactElement;
  /** Whether or not the ActionButton is selected. */
  isSelected?: boolean;
  /**
   * Influences the spacings. Condensed is more compact.
   */
  density?: 'default' | 'condensed';
  /** Handler that is called when the ActionButton is interacted with. */
  onClick?: MouseEventHandler;
  /** Node to be displayed as ActionButton label. */
  children?: ReactNode;
}

/**
 * @public
 */
export type ActionButtonProps<E extends ElementType> =
  PolymorphicComponentProps<E, ActionButtonOwnProps>;
// #endregion

/**
 * An action button used in the ActionGroup.
 * @public
 */
export const ActionButton: <E extends ElementType = 'button'>(
  props: ActionButtonProps<E>,
) => ReactElement | null = forwardRef(
  <E extends ElementType>(
    props: ActionButtonProps<E>,
    forwardedRef: typeof props.ref,
  ) => {
    const {
      showLabel = true,
      isSelected,
      children,
      prefixIcon,
      disabled,
      as,
      pin,
      onClick,
      density = 'default',
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      ...remainingProps
    }: ActionButtonProps<E> = props;
    const { isCollapsed } = useActionGroupContext();
    const { focusClassName, focusProps } = useFocusRing({ disabled });

    /* If the prefixIcon is set and showLabel is false, then the children are shown only in the collapsed version and hidden in the expanded one.
    If there is a prefixIcon and showLabel is true (default), we always show the children.
    If no prefixIcon is set, we always show the children regardless of the showLabel value (otherwise an empty button would be shown). */
    const showChildren = !prefixIcon || showLabel || isCollapsed;

    const sharedProps = {
      disabled,
      className: clsx(
        consumerClassName,
        styles.navItem({
          density,
        }),
        styles.navItemSelectionStyle,
        {
          [styles.disabled]: disabled,
          active: isSelected,
        },
      ),
      'data-testid': dataTestId,
    };

    // The menu item can only be rendered inside a menu, so we have to conditionally render
    // either the menu item or a normal button.
    if (isCollapsed) {
      return (
        <Menu.Item<ElementType>
          ref={forwardedRef}
          as={as}
          onSelect={(onClick ?? (() => null)) as ReactEventHandler}
          {...sharedProps}
          style={consumerStyle}
          {...remainingProps}
        >
          {prefixIcon && <Menu.ItemIcon>{prefixIcon}</Menu.ItemIcon>}
          {showChildren && children}
        </Menu.Item>
      );
    }

    const sharedActionButtonProps = {
      ...sharedProps,
      onClick,
      className: clsx(
        focusClassName,
        textStyleCSS({ textStyle: 'base-emphasized' }),
        sharedProps.className,
      ),
      ...focusProps,
    };

    if (as) {
      const Component = as satisfies ElementType;
      return (
        <Component
          ref={forwardedRef}
          {...sharedActionButtonProps}
          style={consumerStyle}
          {...remainingProps}
        >
          {prefixIcon}
          {showChildren && children}
        </Component>
      );
    }

    return (
      <Button
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ref={forwardedRef as any}
        {...sharedActionButtonProps}
        style={{
          display: 'flex',
          minWidth: 0,
          flexShrink: 0,
          ...consumerStyle,
        }}
        {...remainingProps}
      >
        <Button.Prefix>{prefixIcon}</Button.Prefix>
        {showChildren && children}
      </Button>
    );
  },
);

(ActionButton as typeof ActionButton & { displayName: string }).displayName =
  'ActionGroup.Button';
